import { Check, Users } from "lucide-react";
import { useRef, type KeyboardEvent } from "react";
import { useNavigate } from "react-router";
import { usePersonen } from "../api/abfragen";
import { ZEILE_KLASSE } from "../design/Gruppe";
import { Knopf } from "../design/Knopf";
import { Sheet } from "../design/Sheet";
import { usePersonAnsicht } from "../welt/personAnsicht";

/** W82 Web-5f (D-562, H1, BS07, BS08): Fenster „Personen-Ansicht“ aus dem Menü am Kürzel. Radiogruppe „Alle“ und je Person
 *  der Vorname wie das frühere Segment; die Wahl wirkt sofort und schließt das Fenster, der Fokus geht an das Kürzel zurück
 *  (`fokusNachher`). Pfeile wandern nur (ein Pfeil schlösse sonst das Fenster), Leertaste, Enter und Tipp wählen. Mit nur
 *  einer Person: „Alle“ und der Hinweis auf den Haushalt. Kein Sichern: `gesperrt` bleibt aus (Inventar W51). */
export function PersonAnsichtBlatt(props: { offen: boolean; onSchliessen: () => void; fokusNachher: () => HTMLElement | null }) {
  const { person, setPerson } = usePersonAnsicht();
  const { data: personen } = usePersonen();
  const navigieren = useNavigate();
  const gruppe = useRef<HTMLUListElement>(null);
  const eine = personen !== undefined && personen.length <= 1;
  // H1: mit nur einer Person nur „Alle“ und der Hinweis auf den Haushalt
  const optionen = [{ wert: "alle", name: "Alle" }, ...(eine ? [] : (personen ?? []).map((p) => ({ wert: p.id, name: p.name.split(" ")[0] || p.name })))];

  function waehlen(wert: string) {
    setPerson(wert);
    props.onSchliessen();
  }
  function wandern(e: KeyboardEvent<HTMLButtonElement>, i: number) {
    const schritt = e.key === "ArrowDown" || e.key === "ArrowRight" ? 1 : e.key === "ArrowUp" || e.key === "ArrowLeft" ? -1 : 0;
    if (!schritt) return;
    e.preventDefault();
    const knoepfe = gruppe.current?.querySelectorAll<HTMLButtonElement>('[role="radio"]');
    if (!knoepfe?.length) return;
    knoepfe[(i + schritt + knoepfe.length) % knoepfe.length]?.focus();
  }

  return (
    <Sheet offen={props.offen} onSchliessen={props.onSchliessen} titel="Personen-Ansicht" gesperrt={false} fokusNachher={props.fokusNachher}>
      <ul ref={gruppe} role="radiogroup" aria-label="Personen-Ansicht" className="personen-wahl gruppe mt-2 overflow-clip rounded-group bg-pg-surface">
        {optionen.map((o, i) => {
          const an = o.wert === person;
          // eine Zeile liegt immer in der Tab-Folge, auch wenn die Wahl (noch) keine der Zeilen ist
          const stopp = an || (i === 0 && !optionen.some((x) => x.wert === person));
          return (
            <li key={o.wert} role="none" className="zeile relative">
              <button
                type="button"
                role="radio"
                aria-checked={an}
                // Radiogruppe: nur die gewählte Zeile liegt in der Tab-Folge
                tabIndex={stopp ? 0 : -1}
                onClick={() => waehlen(o.wert)}
                onKeyDown={(e) => wandern(e, i)}
                className={`${ZEILE_KLASSE} items-center`}
              >
                <span className="t-body min-w-0 flex-1 hyphens-auto [overflow-wrap:anywhere]">{o.name}</span>
                {an && <Check size={20} strokeWidth={2} aria-hidden className="shrink-0 text-pg-accent-text" />}
              </button>
            </li>
          );
        })}
      </ul>
      {eine && (
        <div className="personen-hinweis mt-4 flex flex-col items-start gap-2 px-1">
          <p className="t-body text-pg-text-2">Weitere Personen fügst du unter Haushalt hinzu.</p>
          <Knopf
            art="text"
            icon={Users}
            onClick={() => {
              props.onSchliessen();
              navigieren("/haushalt");
            }}
          >
            Zum Haushalt
          </Knopf>
        </div>
      )}
    </Sheet>
  );
}
